import { Dispatch } from 'redux';
import TokenManager from 'Api/TokenManager';
import * as types from 'types';
import { ThunkWithArgs, ThunkNoArgs } from 'state/StateTypes';
import * as userTypes from 'state/user/UserTypes';
import FetchWrapper from 'Api/FetchWrapper';
import * as actionTypes from './UserActionTypes';
import { buildQueryString } from 'utils';
import { RESET_REQUESTED_PATHNAME } from 'state/PathnameReducer';

/**
 * @ThunkWithArgs
 * <typdefs for params, typedefs for payload>
 * @ThunkNoArgs
 * <typedefs for payload>
 * --------------------------------------------
 * 2 separate types are used to avoid unnecessary null checks for actionCreator args
 */
/**
 * SIGNUP
 */
export const signUpUser: ThunkWithArgs<types.SignUpRequest, userTypes.User | null> = ({
   username,
   password,
   firstName,
   lastName,
   phone,
}: types.SignUpRequest) => async (dispatch: Dispatch) => {
   const res = await FetchWrapper({
      path: 'auth/signup',
      mode: 'cors',
      method: 'POST',
      body: {
         username,
         password,
         firstName,
         lastName,
         phone,
      },
   });
   const { jwt, user }: types.LoginResponse = await res.json();
   TokenManager.setAccessToken(jwt.authToken);
   TokenManager.setRefreshToken(jwt.refreshToken);
   return dispatch({ type: actionTypes.USER_LOGIN, payload: user });
};

/**
 * LOGIN
 */

export const loginUser: ThunkWithArgs<types.LoginRequest, userTypes.User | null> = ({ username, password, newPassword }) => async (
   dispatch: Dispatch
) => {
   const res = await FetchWrapper({
      path: 'auth/login',
      mode: 'cors',
      method: 'POST',
      //NOTE undefined object properties are not copied
      body: {
         username,
         password,
         newPassword,
      },
   });
   const { jwt, user }: types.LoginResponse = await res.json();

   TokenManager.setAccessToken(jwt.authToken);
   TokenManager.setRefreshToken(jwt.refreshToken);
   return dispatch({ type: actionTypes.USER_LOGIN, payload: user });
};

//dispatch of type any because payload is not passed
export const logoutUser: ThunkNoArgs<null> = () => async (dispatch: any) => {
   TokenManager.clearTokens();
   dispatch({ type: RESET_REQUESTED_PATHNAME });
   return dispatch({ type: actionTypes.USER_LOGOUT });
};

export const forgotPassword = async ({ username }: types.ForgotPasswordRequest): Promise<void> => {
   await FetchWrapper({
      path: 'auth/forgotpassword',
      mode: 'cors',
      method: 'POST',
      body: {
         username,
      },
   });
   //return value will be empty object
};

export const changePassword = async ({ password, newPassword }: types.ChangePasswordRequest): Promise<string> => {
   const token: string | null = await TokenManager.getAccessToken();
   if (!token) throw new Error('Token is either missing or invalid');

   const res = await FetchWrapper({
      path: 'auth/changepassword',
      mode: 'cors',
      method: 'POST',
      body: {
         password,
         newPassword,
      },
      token,
   });
   const { message }: types.ChangePasswordResponse = await res.json();
   return message;
};

/**
 * USER PROFILE
 */
export const getProfile: ThunkNoArgs<userTypes.User> = () => async (dispatch: Dispatch) => {
   const token: string | null = await TokenManager.getAccessToken();
   if (!token) throw new Error('Token is either missing or invalid');

   try {
      const res = await FetchWrapper({
         path: 'user/profile',
         mode: 'cors',
         method: 'GET',
         token,
      });
      const { user }: userTypes.UserResponse = await res.json();
      return dispatch({ type: actionTypes.GET_PROFILE, payload: user });
   } catch (error) {
      console.log(error);
      return dispatch({ type: actionTypes.GET_PROFILE_ERROR, error });
   }
};
export const updateProfile: ThunkWithArgs<userTypes.UpdateProfileRequest, userTypes.User> = ({ email, firstName, lastName, phone }) => async (
   dispatch: Dispatch
) => {
   const token: string | null = await TokenManager.getAccessToken();
   if (!token) throw new Error('Token is either missing or invalid');
   const res = await FetchWrapper({
      path: 'user/profile',
      mode: 'cors',
      method: 'PATCH',
      token,
      body: {
         email,
         firstName,
         lastName,
         phone,
      },
   });
   const { updatedUser }: userTypes.UpdateProfileResponse = await res.json();
   return dispatch({ type: actionTypes.UPDATE_PROFILE, payload: updatedUser });
};

/**
 * VERIFICATION
 */

//request
export const requestVerification = async ({ attribute }: types.VerificationRequest): Promise<string> => {
   const token: string | null = await TokenManager.getAccessToken();
   if (!token) throw new Error('Token is either missing or invalid');
   const res = await FetchWrapper({
      path: 'auth/verification/request',
      mode: 'cors',
      method: 'POST',
      token,
      body: {
         attribute,
      },
   });
   const { message }: types.VerificationResponse = await res.json();
   return message;
};
//confirm
export const confirmVerification = async (email: string) => {
   await FetchWrapper({
      path: 'auth/verification/confirm',
      mode: 'cors',
      method: 'POST',
      body: {
         email,
      },
   });
};

/**
 * ADMIN
 */
export const createUser = async (user: userTypes.CreateUserRequest) => {
   const token: string | null = await TokenManager.getAccessToken();
   if (!token) throw new Error('Token is either missing or invalid');
   const res = await FetchWrapper({
      path: 'user',
      mode: 'cors',
      method: 'POST',
      token,
      body: user,
   });
   const { createdUser }: userTypes.UserResponse = await res.json();
   return createdUser;
};

export const updateUser = async (user: userTypes.UpdateUserRequest, id: string) => {
   const token: string | null = await TokenManager.getAccessToken();
   if (!token) throw new Error('Token is either missing or invalid');

   const res = await FetchWrapper({
      path: `user/${id}`,
      mode: 'cors',
      method: 'PATCH',
      token,
      body: user,
   });
   const { updatedUser }: userTypes.UserResponse = await res.json();
   return updatedUser;
};

export const deleteUser: ThunkNoArgs<userTypes.User> = () => async (dispatch: Dispatch) => {
   const token: string | null = await TokenManager.getAccessToken();
   if (!token) throw new Error('Token is either missing or invalid');

   try {
      const res = await FetchWrapper({
         path: 'user',
         mode: 'cors',
         method: 'DELETE',
         token,
      });
      const { deletedUser }: userTypes.UserResponse = await res.json();
      return dispatch({ type: actionTypes.DELETE_USER, payload: deletedUser });
   } catch (error) {
      //if error results from missing token it will not have a json method
      error = error.json ? await error.json() : error;
      console.error(actionTypes.DELETE_USER, error);
      return dispatch({ type: actionTypes.DELETE_USER, error: true, payload: error });
   }
};

export const getUser = async (userId: string): Promise<userTypes.User> => {
   const token: string | null = await TokenManager.getAccessToken();
   if (!token) throw new Error('Token is either missing or invalid');

   const res = await FetchWrapper({
      path: `user/${userId}`,
      mode: 'cors',
      method: 'GET',
      token,
   });
   const { user }: userTypes.UserResponse = await res.json();
   //ensure the component only receives the most recent active or expired subscription
   return !!user.subscription.length ? { ...user, subscription: [user.subscription[0]] } : user;
};

export const getUsers = async (
   query: userTypes.GetUsersQueryStrings
): Promise<{ users: userTypes.User[]; totalPages: number; totalDocs: number }> => {
   const token: string | null = await TokenManager.getAccessToken();
   if (!token) throw new Error('Token is either missing or invalid');
   //if there's no search string, remove search field from query
   // query = !query.search && query.searchField ? { ...query, searchField: undefined } : query;
   const queryString = buildQueryString(query);
   const res = await FetchWrapper({
      path: 'user?'.concat(queryString),
      mode: 'cors',
      method: 'GET',
      token,
   });

   const { users, totalPages, totalDocs }: userTypes.GetUsersResponse = await res.json();
   return { users, totalPages, totalDocs };
};

export const getCustomersAsCSV = async (): Promise<string> => {
   const token: string | null = await TokenManager.getAccessToken();
   if (!token) throw new Error('Token is either missing or invalid');

   const res = await FetchWrapper({
      path: 'users/csv',
      mode: 'cors',
      token,
   });

   return (await res.json()) as string;
};
